/* eslint-disable no-use-before-define, react/jsx-key */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useReactTable, getCoreRowModel, flexRender, Cell } from '@tanstack/react-table';
import { useHits } from 'react-instantsearch-hooks-web';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import { AlgoliaProductHit, NormalizedProductHit } from '../../../../types';
import { GridViewHit } from '../GridViewHit';
import { useEtimTranslation } from '../../../../use/etim-translation';
import { getEtimClassNamesFromList } from '../../../../utils/etim-classification';
import EtimClassCodeContext from '../../../../context/EtimClassCodes';
import { ProductTableCellContent } from './ProductTableCellContent';
import { InfoCircle } from '../../../../components/InfoCircle';
import { DangerousHTML } from '../../../../components/DangerousHTML';
import { Tooltip } from '../../../../components/Tooltip';

type ProductsTableProps = {
  normalizeHit: (algoliaHit: AlgoliaProductHit) => NormalizedProductHit;
};

const HeaderCell: FC<{ attribute: string }> = ({ attribute }) => {
  const { name, tooltip, unit } = useEtimTranslation(attribute);

  if (attribute === ' ') {
    return null;
  }

  return (
    <div className="ais-Panel-header__column">
      <span className="ais-Panel-header__title" style={{ fontSize: 11 }}>
        {name}
        {unit ? (
          <span className="ais-Panel-header__unit" style={{ fontSize: 11, textTransform: 'initial' }}>
            {' '}
            [{unit}]
          </span>
        ) : null}
      </span>
      {tooltip && (
        <span style={{ marginLeft: 8 }}>
          <Tooltip tooltipAnchor={<InfoCircle />} tooltipContent={<DangerousHTML content={tooltip} />} />
        </span>
      )}
    </div>
  );
};

const ProductCell = ({
  getValue,
  row,
  isProduct,
  normalizeHit,
  defaultValue,
  isArrowRight,
  displayArrows,
  column,
}: Cell<any, any> & {
  isProduct: boolean;
  normalizeHit: (algoliaHit: AlgoliaProductHit) => NormalizedProductHit;
  defaultValue: string;
  isArrowRight: boolean;
  displayArrows: boolean;
}) => {
  const scrollTableWrapper = document.querySelector('.scroll-table-wrapper');
  const value = getValue();

  const { translateEtimValue } = useEtimTranslation(column.id);

  const scrollStep = 200;

  if (isProduct) {
    const { hit } = row.original;

    const normalizedHit = normalizeHit(hit);

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className="table-product-wrapper">
          <GridViewHit hit={normalizedHit} mobile={false} />
        </div>
        {displayArrows ? (
          <div
            style={{
              width: 20,
              height: 20,
              paddingLeft: 10,
              paddingRight: 10,
              cursor: 'pointer',
            }}
            className="table-container-arrows"
          >
            <a
              className="nav left-scroll"
              onClick={() => {
                if (scrollTableWrapper) {
                  scrollTableWrapper.scrollLeft -= scrollStep;
                }
              }}
            ></a>
          </div>
        ) : null}
      </div>
    );
  }

  if (isArrowRight) {
    return (
      <div
        style={{
          height: 20,
          paddingLeft: 40,
          cursor: 'pointer',
          padding: 0,
          width: 0,
          margin: 0,
        }}
        className="table-container-arrows"
      >
        <a
          className="nav right-scroll"
          onClick={() => {
            if (scrollTableWrapper) {
              scrollTableWrapper.scrollLeft += scrollStep;
            }
          }}
        ></a>
      </div>
    );
  }

  if (typeof value === 'boolean') {
    return <span>{value ? theme.locales.trueLabel : theme.locales.falseLabel}</span>;
  }

  if (value == null) {
    return <span>{defaultValue}</span>;
  }

  if (typeof value === 'string') {
    return (
      (value as string).split(', ').map((etimValue: string, index: number) => (
        <>
          {index !== 0 && ', '}
          <ProductTableCellContent value={etimValue} translateEtimValue={translateEtimValue} />
        </>
      )) ?? value
    );
  }

  return <span>{value}</span>;
};

export const ProductsTable: FC<ProductsTableProps> = ({ normalizeHit }) => {
  const { hits, results } = useHits();
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const checkScrollbarVisibility = () => {
    const element = tableRef.current;

    if (element) {
      setIsScrollbarVisible(element.scrollWidth > element.clientWidth);
    }
  };

  useEffect(() => {
    if (!tableRef.current) {
      return;
    }

    checkScrollbarVisibility();
    const headerObserver = new ResizeObserver(checkScrollbarVisibility);

    headerObserver.observe(tableRef.current);

    document.addEventListener('resize', checkScrollbarVisibility);

    return () => {
      headerObserver.disconnect();
      document.removeEventListener('resize', checkScrollbarVisibility);
    };
  }, [tableRef]);

  const headers = useMemo<string[]>(() => {
    const newFacetAttributes = results?.renderingContent?.facetOrdering?.facets?.order || [];

    const newFilters = getNewFilters(newFacetAttributes);

    if (!newFilters.length) {
      return [theme.locales.product];
    }

    const filteredNewFilters = newFilters
      .filter((attribute) => {
        return (
          attribute &&
          !attribute.startsWith('price') &&
          !['vendor', 'collections', 'tags_algolia', 'availability'].includes(attribute)
        );
      })
      .filter((attribute) => {
        return results?.disjunctiveFacets?.find((facet) => facet.name === attribute) != null;
      })
      .map((filter) => filter.replace('named_tags.', ''));

    return ['product', ...filteredNewFilters, ' '];
  }, [results]);

  const etimClassCodes = useMemo(() => getEtimClassNamesFromList(headers), [headers]);

  const columns = useMemo<any[]>(() => {
    return headers.map((attribute, index) => {
      return {
        accessorKey: attribute,
        header: <HeaderCell attribute={attribute} />,
        cell: (props) =>
          ProductCell({
            isProduct: index === 0,
            normalizeHit,
            defaultValue: '',
            isArrowRight: isScrollbarVisible && index === headers.length - 1,
            displayArrows: isScrollbarVisible,
            ...props,
          }),
        width: index === 0 ? 460 : undefined,
      };
    });
  }, [headers, isScrollbarVisible]);

  const tableData = useMemo(() => {
    return hits.map((hit): Record<string, string> => {
      // @ts-expect-error
      return Object.entries(hit.named_tags).reduce(
        (acc, [tag, value]) => {
          // @ts-expect-error
          if (typeof value === 'object' && value?.length) {
            // @ts-expect-error
            acc[tag] = value?.join(', ');
          } else {
            // @ts-expect-error
            acc[tag] = value;
          }

          return acc;
        },
        { hit }
      );
    });
  }, [hits]);

  const tableInstance = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = tableInstance.getHeaderGroups();
  const { rows } = tableInstance.getRowModel();

  return (
    <EtimClassCodeContext.Provider value={etimClassCodes}>
      <ScrollSync>
        <div>
          <ScrollSyncPane>
            <div
              style={{
                width: '100%',
                marginLeft: 30,
                overflowY: 'hidden',
                overflowX: 'scroll',
                position: 'sticky',
                top: 55,
                zIndex: 1,
                scrollbarGutter: 'stable',
              }}
              className="scroll-table-wrapper"
            >
              <div className="table-header" ref={tableRef}>
                <table className="product-table" style={{ minHeight: 65 }}>
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr>
                        {headerGroup.headers.map((header, index, arr) => (
                          <th
                            style={(() => {
                              if (index === 0) {
                                return { minWidth: 440, paddingRight: 10 };
                              }

                              if (index === arr.length - 1) {
                                return { minWidth: 35, width: 35, padding: 0 };
                              }

                              return {
                                minWidth: header.id.toLowerCase().includes('tf000271') ? 280 : 141,
                              };
                            })()}
                            className={`header-cell-${index}`}
                            key={header.id}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                </table>
              </div>
            </div>
          </ScrollSyncPane>

          <ScrollSyncPane>
            <div className="table-container" style={{ overflow: 'auto' }}>
              <table className="product-table">
                <tbody>
                  {rows.map((row, rowIndex) => {
                    return (
                      <tr
                        onMouseEnter={(event) => {
                          const targetElement = event.target as HTMLElement;
                          const tableRow = targetElement.closest('tr');

                          tableRow?.querySelectorAll<HTMLLinkElement>('.nav').forEach((item) => {
                            item.style.visibility = 'visible';
                          });
                        }}
                        onMouseLeave={(event) => {
                          const targetElement = event.target as HTMLElement;
                          const tableRow = targetElement.closest('tr');

                          tableRow?.querySelectorAll<HTMLLinkElement>('.nav').forEach((item) => {
                            item.style.visibility = 'hidden';
                          });
                        }}
                        className={rowIndex === 0 ? 'first-row' : ''}
                        key={row.id}
                      >
                        {row.getAllCells().map((cell, cellIndex, arr) => {
                          const isLargerCell = cell.id.toLowerCase().includes('tf000271');
                          const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

                          return (
                            <td
                              style={(() => {
                                if (cellIndex === 0) {
                                  return {};
                                }

                                if (cellIndex === arr.length - 1) {
                                  return {
                                    position: 'sticky',
                                    minWidth: 20,
                                    width: 0,
                                    padding: 0,
                                    paddingRight: 15,
                                    right: 0,
                                  };
                                }

                                return {
                                  minWidth: isLargerCell ? 280 : 141,
                                };
                              })()}
                              className={`cell-${cellIndex}`}
                              key={cell.id}
                            >
                              {isLargerCell ? (
                                <div
                                  style={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    lineClamp: 5,
                                    WebkitLineClamp: 5,
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {cellContent}
                                </div>
                              ) : (
                                cellContent
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ScrollSyncPane>
        </div>
      </ScrollSync>
    </EtimClassCodeContext.Provider>
  );
};

const getNewFilters = (newFacetAttributes: Array<string>): Array<string> => {
  return newFacetAttributes.filter((item) => item !== 'collections');
};
